import React from 'react';
import Popover from './Popover';
import ArmorAvatar from './ArmorAvatar';
import { armorLadder, STAGE_AT, STAGE_BUFFER, HALF_LIFE_DAYS, XP_PER_HOUR, XP_DAILY_QUEST, XP_WEEKLY_QUEST, xpForLevel } from '../utils/progress';
import { formatHours } from '../utils/events';

const nfEn = new Intl.NumberFormat('en-GB');
const range = (r) => (r.endLevel === null ? `Lvl ${r.startLevel}+` : `Lvl ${r.startLevel}-${r.endLevel}`);
const Chevron = () => (
  <svg width="16" height="16" fill="none" stroke="rgba(255,255,255,.35)" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);
const Label = ({ children, color = 'rgba(255,255,255,.45)' }) => (
  <span className="uppercase font-semibold" style={{ fontSize: 9, letterSpacing: '.1em', color }}>{children}</span>
);
const rule = { fontSize: 11, lineHeight: '15px', color: 'rgba(255,255,255,.7)' };

// "Your armor": forrige, nuværende og næste rustning, reglerne og hele stigen i miniature.
export default function ArmorPopover({ progress, onClose }) {
  const rows = armorLadder(progress);
  const prev = rows[progress.stage - 2];
  const cur = rows[progress.stage - 1];
  const next = rows[progress.stage];
  const toNextLevel = Math.max(0, (xpForLevel(progress.level + 1) - progress.xp) / XP_PER_HOUR);
  const xpPct = Math.min(100, Math.max(0, (progress.xpIntoLevel / progress.xpForNextLevel) * 100));

  return (
    <Popover title={`Your armor · ${progress.stageName}`} onClose={onClose} width={580} left={-20} caretLeft={50}>
      <div className="grid items-center" style={{ gridTemplateColumns: '1fr auto 1.3fr auto 1fr', gap: 8 }}>
        <div className="flex flex-col items-center" style={{ gap: 4, opacity: 0.6 }}>
          <Label>Previous</Label>
          {prev ? <ArmorAvatar stage={prev.stage} cell={3} /> : <div style={{ height: 66 }} />}
          <span className="font-bold" style={{ fontSize: 12 }}>{prev ? prev.name : '—'}</span>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,.5)' }}>{prev ? range(prev) : 'Start of the journey'}</span>
        </div>
        <Chevron />
        <div className="flex flex-col items-center" style={{ gap: 4, background: '#111e2e', border: '1px solid #FF7800', padding: '10px 12px', borderRadius: 8 }}>
          <Label color="#FF7800">Now · Lvl {progress.level}</Label>
          <ArmorAvatar stage={progress.stage} cell={4} />
          <span className="font-bold" style={{ fontSize: 13 }}>{cur.name}</span>
          <div style={{ width: '100%', height: 6, borderRadius: 3, background: '#273C61' }}>
            <div style={{ height: 6, borderRadius: 3, background: '#94C2FF', width: `${xpPct}%` }} />
          </div>
          <span className="tabular-nums" style={{ fontSize: 10, color: 'rgba(255,255,255,.6)' }}>
            {nfEn.format(progress.xp)} / {nfEn.format(xpForLevel(progress.level + 1))} XP · Lvl {progress.level + 1} in {formatHours(toNextLevel)}
          </span>
        </div>
        <Chevron />
        <div className="flex flex-col items-center" style={{ gap: 4 }}>
          <Label>Next</Label>
          <div style={{ opacity: 0.75 }}>{next ? <ArmorAvatar stage={next.stage} cell={3} /> : <div style={{ height: 66 }} />}</div>
          <span className="font-bold" style={{ fontSize: 12 }}>{next ? next.name : 'Max armor'}</span>
          <span className="font-semibold" style={{ fontSize: 10, color: next ? '#FF7800' : 'rgba(255,255,255,.5)' }}>
            {next ? `${range(next)} · ${formatHours(next.hoursToGo)} to go` : 'reached'}
          </span>
        </div>
      </div>

      <div className="flex flex-col" style={{ gap: 4, marginTop: 14 }}>
        <div style={rule}>
          <b className="text-white">1 hour logged = {XP_PER_HOUR} XP.</b> Daily quest ({formatHours(progress.dayNorm)}) +{XP_DAILY_QUEST} XP · Weekly quest ({formatHours(progress.weekNorm)}) +{nfEn.format(XP_WEEKLY_QUEST)} XP.
        </div>
        <div style={rule}>
          <b className="text-white">Levels get harder as you climb.</b> New armor at Lvl {STAGE_AT.slice(0, -1).join(', ')} and {STAGE_AT[STAGE_AT.length - 1]}.
        </div>
        <div style={rule}>
          <b className="text-white">XP fades</b> with a {HALF_LIFE_DAYS} working-day half-life, so your level follows your recent pace. An armor is only lost {STAGE_BUFFER} levels below its start. Absence pauses the fade.
        </div>
      </div>

      <div className="flex" style={{ gap: 6, marginTop: 12 }}>
        {rows.map((r) => (
          <div key={r.stage} className="flex flex-col items-center" style={{ gap: 3 }}>
            <div
              className="flex items-center justify-center"
              style={{ width: 40, height: 52, borderRadius: 6, background: '#111e2e', border: `1px solid ${r.status === 'current' ? '#FF7800' : '#273C61'}`, opacity: r.status === 'upcoming' ? 0.4 : 1, boxSizing: 'border-box' }}
            >
              <ArmorAvatar stage={r.stage} cell={2} />
            </div>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,.5)' }}>Lvl {r.startLevel}</span>
          </div>
        ))}
      </div>
    </Popover>
  );
}

